<template>
  <div class="card h-100 shadow-sm">
    <div
      class="card-header border-0 pb-0 d-flex flex-column flex-sm-row flex-sm-wrap align-items-sm-center justify-content-between"
    >
      <div>
        <h4 class="card-title mb-1">{{ title }}</h4>
        <p v-if="subtitle" class="text-muted mb-0">{{ subtitle }}</p>
      </div>
      <slot name="action"></slot>
    </div>
    <div class="card-body">
      <div v-if="loading" class="chart-placeholder text-center text-muted">
        <div class="spinner-border text-primary mb-2" role="status"></div>
        <div>Loading insight…</div>
      </div>
      <div
        v-else-if="!hasData"
        class="chart-placeholder text-center text-muted"
      >
        {{ emptyText || "No data available yet" }}
      </div>
      <div v-else>
        <div v-if="type === 'bar'" class="chart-bar" role="list">
          <div
            v-for="point in normalizedBarData"
            :key="point.label"
            class="chart-bar__row"
            role="listitem"
          >
            <span class="chart-bar__label">{{ point.label }}</span>
            <div class="chart-bar__track">
              <div
                class="chart-bar__fill"
                :style="{
                  width: point.percentage + '%',
                  backgroundColor: point.color,
                }"
              ></div>
            </div>
            <span class="chart-bar__value">{{ point.displayValue }}</span>
          </div>
        </div>
        <div v-else-if="type === 'doughnut'" class="chart-doughnut">
          <div class="chart-doughnut__visual" :style="doughnutStyle">
            <div class="chart-doughnut__center">
              <span class="chart-doughnut__total">{{ doughnutTotal }}</span>
              <span class="chart-doughnut__caption">Total</span>
            </div>
          </div>
          <ul class="chart-doughnut__legend">
            <li v-for="slice in doughnutSlices" :key="slice.label">
              <span
                class="legend-dot"
                :style="{ backgroundColor: slice.color }"
              ></span>
              <span class="legend-label">{{ slice.label }}</span>
              <span class="legend-value">{{ slice.displayValue }}</span>
            </li>
          </ul>
        </div>
        <div v-else-if="type === 'combo'" class="chart-combo">
          <svg
            :viewBox="`0 0 ${comboWidth} ${comboHeight}`"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient :id="areaId" x1="0" x2="0" y1="0" y2="1">
                <stop
                  offset="0%"
                  :stop-color="comboColors.revenue"
                  stop-opacity="0.35"
                />
                <stop
                  offset="100%"
                  :stop-color="comboColors.revenue"
                  stop-opacity="0"
                />
              </linearGradient>
            </defs>
            <path
              class="combo-area"
              :d="comboAreaPath"
              fill="url(#areaId)"
              :stroke="comboColors.revenue"
              stroke-width="2"
            ></path>
            <g class="combo-bars">
              <rect
                v-for="bar in comboBars"
                :key="bar.label"
                :x="bar.x"
                :y="bar.y"
                :width="bar.width"
                :height="bar.height"
                :fill="comboColors.orders"
                rx="4"
              ></rect>
            </g>
            <g class="combo-line">
              <path
                :d="comboLinePath"
                fill="none"
                :stroke="comboColors.revenue"
                stroke-width="2.5"
              ></path>
              <circle
                v-for="point in comboPoints"
                :key="point.label"
                :cx="point.x"
                :cy="point.y"
                r="4"
                :fill="comboColors.revenue"
              ></circle>
            </g>
          </svg>
          <div class="chart-combo__axis">
            <span v-for="tick in comboAxis" :key="tick" class="axis-label">{{
              tick
            }}</span>
          </div>
          <div class="chart-combo__legend">
            <span>
              <span
                class="legend-dot"
                :style="{ backgroundColor: comboColors.revenue }"
              ></span>
              Revenue
            </span>
            <span>
              <span
                class="legend-dot"
                :style="{ backgroundColor: comboColors.orders }"
              ></span>
              Orders
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let comboInstance = 0;

export default {
  name: "DashboardChartCard",
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "bar",
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "",
    },
  },
  data() {
    comboInstance += 1;
    return {
      comboWidth: 640,
      comboHeight: 260,
      areaId: `combo-area-${comboInstance}`,
      comboColors: {
        revenue: "#4f46e5",
        orders: "#f97316",
      },
    };
  },
  computed: {
    hasData() {
      return Array.isArray(this.chartData) && this.chartData.length > 0;
    },
    maxBarValue() {
      if (!this.hasData || this.type !== "bar") {
        return 0;
      }
      return Math.max(...this.chartData.map((point) => point.value));
    },
    normalizedBarData() {
      if (this.type !== "bar" || !this.hasData) {
        return [];
      }
      const max = this.maxBarValue || 1;
      return this.chartData.map((point) => ({
        ...point,
        percentage: Math.min(100, Math.round((point.value / max) * 100)),
      }));
    },
    doughnutTotalValue() {
      if (this.type !== "doughnut" || !this.hasData) {
        return 0;
      }
      return this.chartData.reduce((sum, slice) => sum + (slice.value || 0), 0);
    },
    doughnutSlices() {
      if (this.type !== "doughnut" || !this.hasData) {
        return [];
      }
      return this.chartData;
    },
    doughnutStyle() {
      if (this.type !== "doughnut" || !this.hasData) {
        return {};
      }
      const total = this.doughnutTotalValue || 1;
      let accumulated = 0;
      const segments = this.chartData
        .map((slice) => {
          const start = (accumulated / total) * 360;
          accumulated += slice.value || 0;
          const end = (accumulated / total) * 360;
          return `${slice.color} ${start}deg ${end}deg`;
        })
        .join(", ");
      return {
        background: `conic-gradient(${segments})`,
      };
    },
    doughnutTotal() {
      if (this.type !== "doughnut" || !this.hasData) {
        return "--";
      }
      if (this.chartData[0].displayTotal) {
        return this.chartData[0].displayTotal;
      }
      return this.doughnutTotalValue.toLocaleString();
    },
    comboMetrics() {
      if (this.type !== "combo" || !this.hasData) {
        return {
          points: [],
          bars: [],
          areaPath: "",
          linePath: "",
          axis: [],
        };
      }
      const paddingX = 24;
      const paddingY = 24;
      const usableWidth = this.comboWidth - paddingX * 2;
      const usableHeight = this.comboHeight - paddingY * 2;
      const step = usableWidth / Math.max(this.chartData.length - 1, 1);
      const maxRevenue = Math.max(
        ...this.chartData.map((point) => point.revenue)
      );
      const maxOrders = Math.max(
        ...this.chartData.map((point) => point.orders)
      );
      const axis = this.chartData.map((point) => point.label);
      const points = this.chartData.map((point, index) => {
        const x = paddingX + step * index;
        const ratio = maxRevenue ? point.revenue / maxRevenue : 0;
        const y = paddingY + (1 - ratio) * usableHeight;
        return { x, y, label: point.label };
      });
      const bars = this.chartData.map((point, index) => {
        const barWidth = Math.max(8, step * 0.4);
        const x = paddingX + step * index - barWidth / 2;
        const ratio = maxOrders ? point.orders / maxOrders : 0;
        const height = ratio * usableHeight;
        const y = paddingY + usableHeight - height;
        return { x, y, width: barWidth, height, label: point.label };
      });
      const linePath = points
        .map(
          (point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`
        )
        .join(" ");
      const areaPath = `${linePath} L${
        paddingX + step * (this.chartData.length - 1)
      },${paddingY + usableHeight} L${paddingX},${paddingY + usableHeight} Z`;
      return {
        points,
        bars,
        areaPath,
        linePath,
        axis,
      };
    },
    comboAreaPath() {
      return this.comboMetrics.areaPath;
    },
    comboLinePath() {
      return this.comboMetrics.linePath;
    },
    comboPoints() {
      return this.comboMetrics.points;
    },
    comboBars() {
      return this.comboMetrics.bars;
    },
    comboAxis() {
      return this.comboMetrics.axis;
    },
  },
};
</script>

<style scoped>
.chart-placeholder {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.chart-bar__row {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(120px, 2fr) minmax(
      90px,
      0.8fr
    );
  gap: 0.75rem;
  align-items: center;
}

.chart-bar__label {
  font-weight: 500;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chart-bar__track {
  background-color: #f1f5f9;
  height: 10px;
  border-radius: 9999px;
  overflow: hidden;
}

.chart-bar__fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.35s ease;
}

.chart-bar__value {
  text-align: right;
  font-weight: 600;
}

.chart-doughnut {
  display: grid;
  grid-template-columns: minmax(0, 200px) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: center;
}

.chart-doughnut__visual {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 50%;
}

.chart-doughnut__visual::after {
  content: "";
  position: absolute;
  inset: 24px;
  background-color: #fff;
  border-radius: 50%;
}

.chart-doughnut__center {
  position: absolute;
  inset: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.25rem;
  border-radius: 50%;
}

.chart-doughnut__total {
  font-size: 1.4rem;
  font-weight: 600;
}

.chart-doughnut__caption {
  font-size: 0.85rem;
  color: #64748b;
}

.chart-doughnut__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.chart-doughnut__legend li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-label {
  font-weight: 500;
  color: #334155;
}

.legend-value {
  margin-left: auto;
  font-weight: 600;
}

.chart-combo {
  width: 100%;
}

.chart-combo svg {
  width: 100%;
  height: auto;
}

.combo-area {
  fill-opacity: 0.18;
}

.chart-combo__axis {
  margin-top: 0.75rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #64748b;
}

.chart-combo__legend {
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1.5rem;
  font-size: 0.9rem;
  color: #475569;
}

@media (max-width: 991px) {
  .chart-doughnut {
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .chart-doughnut__legend {
    width: 100%;
  }
}
</style>
